import { z } from "zod";

const IdentifierSchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
const CountryCodeSchema = z.string().regex(/^[A-Z]{2}$/);
const TimestampSchema = z.iso.datetime({ offset: true });
const HexColorSchema = z.string().regex(/^#[0-9A-F]{6}$/);
const UrlSchema = z.url().refine((value) => {
  const protocol = new URL(value).protocol;
  return protocol === "https:" || protocol === "http:";
}, "Only HTTP and HTTPS evidence URLs are allowed.");

function isIanaTimezone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

export const CoordinatesSchema = z.tuple([
  z.number().min(-180).max(180),
  z.number().min(-90).max(90),
]);

export const ConfidenceBandSchema = z.enum(["low", "medium", "high"]);
export const EvidenceTypeSchema = z.enum([
  "driver-self-disclosure",
  "public-appearance",
  "reputable-press",
  "associate-context",
  "completed-travel-report",
]);

export const EvidenceReferenceSchema = z
  .object({
    id: IdentifierSchema,
    sourceId: IdentifierSchema,
    sourceGroupId: IdentifierSchema,
    publisher: z.string().trim().min(1).max(100),
    title: z.string().trim().min(1).max(180),
    url: UrlSchema,
    evidenceType: EvidenceTypeSchema,
    publishedAt: TimestampSchema,
    observedAt: TimestampSchema,
    expiresAt: TimestampSchema,
    freshness: z.enum(["fresh", "aging"]),
  })
  .strict()
  .refine(
    (value) => Date.parse(value.expiresAt) > Date.parse(value.observedAt),
    {
      message: "Evidence must expire after it was observed.",
      path: ["expiresAt"],
    },
  );

export const PublicAppearanceSchema = z
  .object({
    label: z.string().trim().min(1).max(120),
    city: z.string().trim().min(1).max(100),
    countryCode: CountryCodeSchema,
    coordinates: CoordinatesSchema,
    startsAt: TimestampSchema,
    endsAt: TimestampSchema,
    sourceId: IdentifierSchema,
    sourceUrl: UrlSchema,
  })
  .strict()
  .refine((value) => Date.parse(value.endsAt) > Date.parse(value.startsAt), {
    message: "A public appearance must end after it starts.",
    path: ["endsAt"],
  });

const LocationBaseShape = {
  driverId: IdentifierSchema,
  precision: z.literal("city"),
  evidence: z.array(EvidenceReferenceSchema).max(12),
  rationale: z.string().trim().min(1).max(280),
  expectedNext: PublicAppearanceSchema.optional(),
};

export const EstimatedDriverLocationSchema = z
  .object({
    ...LocationBaseShape,
    status: z.literal("estimated"),
    city: z.string().trim().min(1).max(100),
    countryCode: CountryCodeSchema,
    coordinates: CoordinatesSchema,
    confidenceScore: z.number().int().min(40).max(100),
    confidenceBand: ConfidenceBandSchema,
    observedAt: TimestampSchema,
    expiresAt: TimestampSchema,
    evidence: z.array(EvidenceReferenceSchema).min(1).max(12),
  })
  .strict()
  .superRefine((value, context) => {
    if (Date.parse(value.expiresAt) <= Date.parse(value.observedAt)) {
      context.addIssue({
        code: "custom",
        message: "An estimate must expire after it was observed.",
        path: ["expiresAt"],
      });
    }

    const expectedBand =
      value.confidenceScore >= 80
        ? "high"
        : value.confidenceScore >= 60
          ? "medium"
          : "low";

    if (value.confidenceBand !== expectedBand) {
      context.addIssue({
        code: "custom",
        message: "The confidence band must match the audit score.",
        path: ["confidenceBand"],
      });
    }
  });

export const UnknownDriverLocationSchema = z
  .object({
    ...LocationBaseShape,
    status: z.literal("unknown"),
  })
  .strict();

export const DriverLocationSchema = z.discriminatedUnion("status", [
  EstimatedDriverLocationSchema,
  UnknownDriverLocationSchema,
]);

export const DriverSchema = z
  .object({
    id: IdentifierSchema,
    givenName: z.string().trim().min(1).max(80),
    familyName: z.string().trim().min(1).max(80),
    acronym: z.string().regex(/^[A-Z]{3}$/),
    permanentNumber: z.string().regex(/^\d{1,2}$/),
    constructorId: IdentifierSchema,
    accentColor: HexColorSchema,
    active: z.boolean(),
  })
  .strict();

export const SeasonSummarySchema = z
  .object({
    year: z.number().int().min(1950).max(2200),
    championshipName: z.string().trim().min(1).max(120),
    rosterUpdatedAt: TimestampSchema,
  })
  .strict();

export const RaceSessionSchema = z
  .object({
    id: IdentifierSchema,
    kind: z.enum([
      "practice-1",
      "practice-2",
      "practice-3",
      "sprint-qualifying",
      "sprint",
      "qualifying",
      "race",
    ]),
    label: z.string().trim().min(1).max(80),
    startsAt: TimestampSchema,
    endsAt: TimestampSchema,
    status: z.enum(["scheduled", "live", "completed", "delayed", "cancelled"]),
  })
  .strict()
  .refine((value) => Date.parse(value.endsAt) > Date.parse(value.startsAt), {
    message: "A race session must end after it starts.",
    path: ["endsAt"],
  });

const SessionResultEntrySchema = z
  .object({
    driverId: IdentifierSchema,
    constructorId: IdentifierSchema,
    finishPosition: z.number().int().positive().nullable(),
    points: z.number().nonnegative(),
    grid: z.number().int().nonnegative().optional(),
    laps: z.number().int().nonnegative().optional(),
    status: z.string().trim().min(1).max(80).optional(),
    finishTimeMilliseconds: z.number().int().nonnegative().optional(),
    finishTime: z.string().trim().min(1).max(40).optional(),
    interval: z.string().trim().min(1).max(40).optional(),
    fastestLap: z
      .object({
        rank: z.number().int().positive().optional(),
        lap: z.number().int().positive().optional(),
        time: z.string().trim().min(1).max(40),
      })
      .strict()
      .optional(),
    qualifyingTimes: z
      .object({
        q1: z.string().trim().min(1).max(40).optional(),
        q2: z.string().trim().min(1).max(40).optional(),
        q3: z.string().trim().min(1).max(40).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

export const SessionResultSchema = z
  .object({
    sessionId: IdentifierSchema,
    circuitId: IdentifierSchema,
    season: z.number().int().min(1950).max(2200),
    round: z.number().int().positive(),
    kind: z.enum(["sprint", "qualifying", "race"]),
    label: z.string().trim().min(1).max(80),
    completedAt: TimestampSchema,
    sourceId: IdentifierSchema,
    entries: z.array(SessionResultEntrySchema).min(1).max(40),
  })
  .strict();

export const CircuitArchiveEntrySchema = z
  .object({
    driverId: IdentifierSchema,
    driverName: z.string().trim().min(1).max(120),
    constructorId: IdentifierSchema,
    constructorName: z.string().trim().min(1).max(120),
    finishPosition: z.number().int().positive().nullable(),
    points: z.number().nonnegative(),
    grid: z.number().int().nonnegative().optional(),
    laps: z.number().int().nonnegative().optional(),
    status: z.string().trim().min(1).max(80).optional(),
    finishTimeMilliseconds: z.number().int().nonnegative().optional(),
    finishTime: z.string().trim().min(1).max(40).optional(),
    interval: z.string().trim().min(1).max(40).optional(),
    fastestLap: z
      .object({
        lap: z.number().int().positive().optional(),
        time: z.string().trim().min(1).max(40),
      })
      .strict()
      .optional(),
    qualifyingTimes: z
      .object({
        q1: z.string().trim().min(1).max(40).optional(),
        q2: z.string().trim().min(1).max(40).optional(),
        q3: z.string().trim().min(1).max(40).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

export const CircuitArchiveSessionSchema = z
  .object({
    kind: z.enum([
      "practice-1",
      "practice-2",
      "practice-3",
      "qualifying",
      "sprint-qualifying",
      "sprint",
      "race",
    ]),
    label: z.string().trim().min(1).max(80),
    completedAt: TimestampSchema,
    entries: z.array(CircuitArchiveEntrySchema).min(1).max(40),
  })
  .strict();

export const CircuitArchiveRaceSchema = z
  .object({
    season: z.number().int().min(1950).max(2200),
    round: z.number().int().positive(),
    circuitId: IdentifierSchema,
    circuitName: z.string().trim().min(1).max(160),
    raceName: z.string().trim().min(1).max(160),
    completedAt: TimestampSchema,
    sessions: z.array(CircuitArchiveSessionSchema).min(1).max(8),
  })
  .strict();

export const CircuitArchiveSchema = z
  .object({
    sourceId: z.literal("f1db"),
    fetchedAt: TimestampSchema,
    releaseTag: z.string().regex(/^v\d{4}\.\d+\.\d+$/),
    races: z.array(CircuitArchiveRaceSchema).max(3_000),
  })
  .strict();

export type CircuitArchive = z.infer<typeof CircuitArchiveSchema>;

export const RaceWeekendSchema = z
  .object({
    id: IdentifierSchema,
    round: z.number().int().positive(),
    name: z.string().trim().min(1).max(120),
    circuitId: IdentifierSchema,
    timezone: z.string().trim().min(1).max(80),
    startsAt: TimestampSchema,
    endsAt: TimestampSchema,
    status: z.enum(["upcoming", "live"]),
    sessions: z.array(RaceSessionSchema).min(1).max(8),
  })
  .strict()
  .superRefine((value, context) => {
    const startsAt = Date.parse(value.startsAt);
    const endsAt = Date.parse(value.endsAt);
    if (endsAt <= startsAt) {
      context.addIssue({
        code: "custom",
        message: "A race weekend must end after it starts.",
        path: ["endsAt"],
      });
    }
    if (!isIanaTimezone(value.timezone)) {
      context.addIssue({
        code: "custom",
        message: "A race weekend requires a valid IANA timezone.",
        path: ["timezone"],
      });
    }
    if (
      duplicateValues(value.sessions.map((session) => session.id)).length > 0
    ) {
      context.addIssue({
        code: "custom",
        message: "Race session IDs must be unique within a weekend.",
        path: ["sessions"],
      });
    }
    if (
      value.sessions.some(
        (session) =>
          Date.parse(session.startsAt) < startsAt ||
          Date.parse(session.endsAt) > endsAt,
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "Race sessions must occur within the weekend window.",
        path: ["sessions"],
      });
    }
  });

export const DriverStandingSchema = z
  .object({
    position: z.number().int().positive(),
    driverId: IdentifierSchema,
    constructorId: IdentifierSchema,
    points: z.number().nonnegative(),
    wins: z.number().int().nonnegative(),
  })
  .strict();

export const ConstructorStandingSchema = z
  .object({
    position: z.number().int().positive(),
    constructorId: IdentifierSchema,
    name: z.string().trim().min(1).max(100),
    accentColor: HexColorSchema,
    points: z.number().nonnegative(),
    wins: z.number().int().nonnegative(),
  })
  .strict();

export const ChampionshipStandingsSchema = z
  .object({
    publishedAt: TimestampSchema,
    sourceId: IdentifierSchema,
    drivers: z.array(DriverStandingSchema).max(30),
    constructors: z.array(ConstructorStandingSchema).max(20),
  })
  .strict();

export const DriverRoundProgressSchema = z
  .object({
    driverId: IdentifierSchema,
    finishPosition: z.number().int().positive().nullable(),
    racePoints: z.number().nonnegative(),
    sprintPoints: z.number().nonnegative(),
    cumulativePoints: z.number().nonnegative(),
    championshipPosition: z.number().int().positive(),
  })
  .strict();

export const ConstructorRoundProgressSchema = z
  .object({
    constructorId: IdentifierSchema,
    racePoints: z.number().nonnegative(),
    sprintPoints: z.number().nonnegative(),
    cumulativePoints: z.number().nonnegative(),
    championshipPosition: z.number().int().positive(),
  })
  .strict();

export const ChampionshipRoundProgressSchema = z
  .object({
    round: z.number().int().positive(),
    raceName: z.string().trim().min(1).max(120),
    circuitId: IdentifierSchema,
    completedAt: TimestampSchema,
    drivers: z.array(DriverRoundProgressSchema).max(30),
    constructors: z.array(ConstructorRoundProgressSchema).max(20),
  })
  .strict();

export const ChampionshipProgressionSchema = z
  .object({
    sourceId: IdentifierSchema,
    publishedAt: TimestampSchema,
    rounds: z.array(ChampionshipRoundProgressSchema).max(30),
  })
  .strict();

export const CircuitSummarySchema = z
  .object({
    id: IdentifierSchema,
    round: z.number().int().positive(),
    name: z.string().trim().min(1).max(120),
    city: z.string().trim().min(1).max(100),
    countryCode: CountryCodeSchema,
    coordinates: CoordinatesSchema,
    status: z.enum(["past", "current", "upcoming"]),
    assetState: z.enum(["3d", "2d-fallback", "unavailable"]),
    geometryAttribution: z.string().trim().min(1).max(180),
    elevationAttribution: z.string().trim().min(1).max(180).optional(),
    bankingAttribution: z.string().trim().min(1).max(180).optional(),
  })
  .strict();

export const SourceHealthSchema = z
  .object({
    sourceId: IdentifierSchema,
    status: z.enum(["healthy", "degraded", "disabled"]),
    checkedAt: TimestampSchema,
    lastSuccessfulAt: TimestampSchema.optional(),
    message: z.string().trim().min(1).max(180).optional(),
  })
  .strict();

function duplicateValues(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  for (const value of values) {
    if (seen.has(value)) {
      duplicates.add(value);
    }
    seen.add(value);
  }

  return [...duplicates];
}

export const PublicSnapshotSchema = z
  .object({
    schemaVersion: z.union([z.literal(1), z.literal(2)]),
    id: IdentifierSchema,
    generatedAt: TimestampSchema,
    staleAt: TimestampSchema,
    season: SeasonSummarySchema,
    drivers: z.array(DriverSchema).min(1).max(30),
    locations: z.array(DriverLocationSchema).max(30),
    weekend: RaceWeekendSchema.nullable(),
    standings: ChampionshipStandingsSchema,
    progression: ChampionshipProgressionSchema.optional(),
    sessionResults: z.array(SessionResultSchema).max(400).optional(),
    circuits: z.array(CircuitSummarySchema).max(30),
    sourceHealth: z.array(SourceHealthSchema).max(50),
  })
  .strict()
  .superRefine((snapshot, context) => {
    if (snapshot.schemaVersion === 2 && snapshot.sessionResults === undefined) {
      context.addIssue({
        code: "custom",
        message: "Schema version 2 snapshots must publish session results.",
        path: ["sessionResults"],
      });
    }
    if (snapshot.schemaVersion === 1 && snapshot.sessionResults !== undefined) {
      context.addIssue({
        code: "custom",
        message: "Schema version 1 snapshots cannot publish session results.",
        path: ["sessionResults"],
      });
    }
    if (Date.parse(snapshot.staleAt) <= Date.parse(snapshot.generatedAt)) {
      context.addIssue({
        code: "custom",
        message: "A snapshot must become stale after it is generated.",
        path: ["staleAt"],
      });
    }

    const driverIds = new Set(snapshot.drivers.map((driver) => driver.id));
    const constructorIdValues = snapshot.standings.constructors.map(
      (constructor) => constructor.constructorId,
    );
    const constructorIds = new Set(constructorIdValues);
    const circuitIdValues = snapshot.circuits.map((circuit) => circuit.id);
    const circuitIds = new Set(circuitIdValues);
    const sourceIdValues = snapshot.sourceHealth.map(
      (source) => source.sourceId,
    );
    const sourceIds = new Set(sourceIdValues);
    const locationDriverIds = snapshot.locations.map(
      (location) => location.driverId,
    );

    for (const duplicate of duplicateValues(
      snapshot.drivers.map((driver) => driver.id),
    )) {
      context.addIssue({
        code: "custom",
        message: `Duplicate driver ${duplicate}.`,
        path: ["drivers"],
      });
    }

    for (const duplicate of duplicateValues(locationDriverIds)) {
      context.addIssue({
        code: "custom",
        message: `Duplicate location record for ${duplicate}.`,
        path: ["locations"],
      });
    }

    for (const [values, path, label] of [
      [constructorIdValues, "constructors", "constructor"],
      [circuitIdValues, "circuits", "circuit"],
      [sourceIdValues, "sourceHealth", "source"],
    ] as const) {
      for (const duplicate of duplicateValues(values)) {
        context.addIssue({
          code: "custom",
          message: `Duplicate ${label} ${duplicate}.`,
          path: [path],
        });
      }
    }

    for (const driver of snapshot.drivers) {
      if (!constructorIds.has(driver.constructorId)) {
        context.addIssue({
          code: "custom",
          message: `Driver ${driver.id} references an unknown constructor.`,
          path: ["drivers"],
        });
      }
    }

    for (const driver of snapshot.drivers.filter(
      (candidate) => candidate.active,
    )) {
      if (!locationDriverIds.includes(driver.id)) {
        context.addIssue({
          code: "custom",
          message: `Active driver ${driver.id} must have a location or Unknown record.`,
          path: ["locations"],
        });
      }
    }

    for (const location of snapshot.locations) {
      if (!driverIds.has(location.driverId)) {
        context.addIssue({
          code: "custom",
          message: `Location references unknown driver ${location.driverId}.`,
          path: ["locations"],
        });
      }

      if (location.status === "estimated") {
        if (
          Date.parse(location.observedAt) > Date.parse(snapshot.generatedAt)
        ) {
          context.addIssue({
            code: "custom",
            message:
              "An estimate cannot be observed after its snapshot is generated.",
            path: ["locations"],
          });
        }
        if (
          Date.parse(location.expiresAt) <= Date.parse(snapshot.generatedAt) ||
          location.evidence.some(
            (evidence) =>
              Date.parse(evidence.expiresAt) <=
              Date.parse(snapshot.generatedAt),
          )
        ) {
          context.addIssue({
            code: "custom",
            message:
              "Published estimates and their evidence must be current at generation time.",
            path: ["locations"],
          });
        }
      }

      const evidenceSourceIds = location.evidence.map(
        (evidence) => evidence.sourceId,
      );
      if (location.expectedNext) {
        evidenceSourceIds.push(location.expectedNext.sourceId);
      }
      for (const sourceId of evidenceSourceIds) {
        if (!sourceIds.has(sourceId)) {
          context.addIssue({
            code: "custom",
            message: `Location references unknown source ${sourceId}.`,
            path: ["locations"],
          });
        }
      }
    }

    const evidenceIds = snapshot.locations.flatMap((location) =>
      location.evidence.map((evidence) => evidence.id),
    );
    if (duplicateValues(evidenceIds).length > 0) {
      context.addIssue({
        code: "custom",
        message: "Evidence IDs must be unique within a snapshot.",
        path: ["locations"],
      });
    }

    if (
      duplicateValues(
        snapshot.standings.drivers.map((standing) => standing.driverId),
      ).length > 0 ||
      duplicateValues(
        snapshot.standings.drivers.map((standing) => String(standing.position)),
      ).length > 0 ||
      duplicateValues(
        snapshot.standings.constructors.map((standing) =>
          String(standing.position),
        ),
      ).length > 0
    ) {
      context.addIssue({
        code: "custom",
        message:
          "Standing identities and positions must be unique within each table.",
        path: ["standings"],
      });
    }

    for (const standing of snapshot.standings.drivers) {
      if (
        !driverIds.has(standing.driverId) ||
        !constructorIds.has(standing.constructorId)
      ) {
        context.addIssue({
          code: "custom",
          message:
            "Driver standings must reference known drivers and constructors.",
          path: ["standings", "drivers"],
        });
      }
    }

    if (!sourceIds.has(snapshot.standings.sourceId)) {
      context.addIssue({
        code: "custom",
        message: "Standings must reference a known source.",
        path: ["standings", "sourceId"],
      });
    }

    if (snapshot.progression !== undefined) {
      if (!sourceIds.has(snapshot.progression.sourceId)) {
        context.addIssue({
          code: "custom",
          message: "Championship progression must reference a known source.",
          path: ["progression", "sourceId"],
        });
      }
      for (const round of snapshot.progression.rounds) {
        if (!circuitIds.has(round.circuitId)) {
          context.addIssue({
            code: "custom",
            message: `Progression round ${String(round.round)} references an unknown circuit.`,
            path: ["progression", "rounds"],
          });
        }
        if (round.drivers.some((entry) => !driverIds.has(entry.driverId))) {
          context.addIssue({
            code: "custom",
            message: "Progression references an unknown driver.",
            path: ["progression", "rounds", "drivers"],
          });
        }
        if (
          round.constructors.some(
            (entry) => !constructorIds.has(entry.constructorId),
          )
        ) {
          context.addIssue({
            code: "custom",
            message: "Progression references an unknown constructor.",
            path: ["progression", "rounds", "constructors"],
          });
        }
      }
    }

    if (snapshot.sessionResults !== undefined) {
      for (const result of snapshot.sessionResults) {
        if (!circuitIds.has(result.circuitId)) {
          context.addIssue({
            code: "custom",
            message: `Session result references unknown circuit ${result.circuitId}.`,
            path: ["sessionResults"],
          });
        }
        for (const entry of result.entries) {
          if (
            !driverIds.has(entry.driverId) ||
            !constructorIds.has(entry.constructorId)
          ) {
            context.addIssue({
              code: "custom",
              message:
                "Session results must reference known drivers and constructors.",
              path: ["sessionResults"],
            });
          }
        }
      }
    }

    if (snapshot.weekend && !circuitIds.has(snapshot.weekend.circuitId)) {
      context.addIssue({
        code: "custom",
        message: "The active weekend must reference a known circuit.",
        path: ["weekend", "circuitId"],
      });
    }
  });

export type PublicSnapshot = z.infer<typeof PublicSnapshotSchema>;
export type DriverLocation = z.infer<typeof DriverLocationSchema>;
export type EvidenceType = z.infer<typeof EvidenceTypeSchema>;
export type EstimatedDriverLocation = z.infer<
  typeof EstimatedDriverLocationSchema
>;
export type UnknownDriverLocation = z.infer<typeof UnknownDriverLocationSchema>;
export type DriverLocationPresentation =
  | Omit<EstimatedDriverLocation, "confidenceScore">
  | UnknownDriverLocation;
